import { PATH } from '@/constants/path';
import { useAnalytics } from '@/utils/useAnalytics';
import Link from 'next/link';
import React from 'react';

import styles from './AnniversaryBanner.module.scss';
import AnniversaryLogo from './assets/AnniversaryLogo.svg';
import { useAnniversaryBanner } from './useAnniversaryBanner';

const STORY_URL = `${PATH.ABOUT_US}/celebrating-20-years`;
const CTA_TEXT = 'See our story';

export type AnniversaryBannerContentProps = {
  onClose: () => void;
  onStoryClick?: () => void;
};

export const AnniversaryBannerContent = ({
  onClose,
  onStoryClick,
}: AnniversaryBannerContentProps) => (
  <div
    className={styles.banner}
    role="region"
    aria-label="20 year anniversary"
    // Lets page backgrounds positioned from the top of the page make room for the banner
    data-anniversary-banner
  >
    <Link
      prefetch={false}
      href={STORY_URL}
      className={styles.logoLink}
      aria-label="Celebrating 20 years in good hands"
      onClick={onStoryClick}
    >
      {/* viewBox passed explicitly as SVGO strips it, and the logo scales down on small screens */}
      <AnniversaryLogo className={styles.logo} viewBox="0 0 304 45" aria-hidden />
    </Link>

    <div className={styles.actions}>
      <Link prefetch={false} href={STORY_URL} className={styles.cta} onClick={onStoryClick}>
        {CTA_TEXT}
      </Link>
      <button
        type="button"
        className={styles.closeButton}
        aria-label="Dismiss banner"
        onClick={onClose}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path
            d="M1 1L13 13M13 1L1 13"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  </div>
);

const AnniversaryBanner = () => {
  const track = useAnalytics();
  const { isVisible, dismiss } = useAnniversaryBanner();

  if (!isVisible) {
    return null;
  }

  return (
    <AnniversaryBannerContent
      onStoryClick={() =>
        track('Click', {
          action: 'Anniversary banner - see our story',
          'element text': CTA_TEXT,
          'element type': 'link',
        })
      }
      onClose={() => {
        dismiss();
        track('Click', {
          action: 'Close anniversary banner',
          'element text': '',
          'element type': 'button',
        });
      }}
    />
  );
};

export default AnniversaryBanner;
